import { CSSProperties, useEffect, useRef } from 'react';
import { cn } from '../../../lib/utils';

declare global {
  interface Window {
    lottie?: {
      loadAnimation: (params: {
        container: Element;
        loop: boolean;
        autoplay: boolean;
        animationData: object;
        renderer: string;
        rendererSettings: { preserveAspectRatio: string };
      }) => { destroy: () => void };
    };
  }
}

/**
 * Spinner — Lottie-based loading indicator matching the Sekretly brand
 * animation (SekretlyLoading3_dark / _light).
 *
 * Pass `animationData` (pre-loaded JSON) for the branded animation.
 * Without it, falls back to a minimal CSS ring (useful during SSR or when
 * lottie-web hasn't loaded yet).
 *
 * Sizes map to the style guide's three canonical sizes:
 *   sm → 32px  md → 48px  lg → 72px
 *
 * The branded Lottie JSON assets (SekretlyLoading3_dark/_light.json) live in
 * `ScreenLoader`'s assets/ folder; wire `animationData` up to one of them
 * (plus the `lottie-web` dependency) when a call site actually wants the
 * branded animation instead of the CSS ring.
 */
export interface SpinnerProps {
  /** Default 'md'. */
  size?: 'sm' | 'md' | 'lg';
  /** Pre-loaded Lottie JSON. When omitted, renders the CSS ring fallback. */
  animationData?: object;
  /** CSS color for the ring fallback. Defaults to currentColor. */
  color?: string;
  className?: string;
  style?: CSSProperties;
}

export function Spinner({ size = 'md', animationData, color, className, style }: SpinnerProps) {
  const px = { sm: 32, md: 48, lg: 72 }[size] ?? 48;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!animationData || !ref.current || !window.lottie) return;
    const anim = window.lottie.loadAnimation({
      container: ref.current,
      loop: true,
      autoplay: true,
      animationData,
      renderer: 'svg',
      rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
    });
    return () => anim.destroy();
  }, [animationData]);

  if (animationData) {
    return (
      <div
        ref={ref}
        role="status"
        aria-label="Loading"
        className={cn(className)}
        style={{ width: px, height: px, flexShrink: 0, ...style }}
      />
    );
  }

  // CSS ring fallback — used when animationData is not yet available
  const stroke = px <= 32 ? 2 : px <= 48 ? 2.5 : 3;
  const c = color || 'currentColor';
  return (
    <span role="status" aria-label="Loading" className={cn(className)} style={{ display: 'inline-block', width: px, height: px, flexShrink: 0, ...style }}>
      <svg width={px} height={px} viewBox="0 0 24 24" fill="none" style={{ animation: 'sk-spin .7s linear infinite' }}>
        <circle cx="12" cy="12" r="9" stroke={c} strokeOpacity="0.2" strokeWidth={stroke} />
        <path d="M21 12a9 9 0 0 0-9-9" stroke={c} strokeWidth={stroke} strokeLinecap="round" />
      </svg>
      <style>{`@keyframes sk-spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

export default Spinner;
